import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAccountNumbersOptions from '@/hooks/useAccountNumbersOptions';
import useAlerts from '@/hooks/useAlerts';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import useFreelancerRolesOptions from '@/hooks/useFreelancerRolesOptions';
import useFreelancerSubunitsOptions from '@/hooks/useFreelancerSubunitsOptions';
import {
  FreelancerDepartmentDetailsResource,
  FreelancerUserResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import {
  contractTypes,
  employmentTypes,
  statuses,
  workFormats,
  workSpaces,
} from '@/utils/data/payments';
import { handleFormErrors } from '@/utils/helpers';

export type FormDataT = {
  company_ids: number[];
  department_id: number | null;
  subunit_id: number | null;
  finance_account_number_id: number | null;
  role_id: number | null;
  team_lead: string;
  team_lead_id: number;
  employment_type?: string;
  started_at?: string;
  ended_at?: string;
  contract_type?: string;
  trial_ended_at?: string;
  work_space?: string;
  work_format?: string;
  bonuses?: boolean;
  benefits?: boolean;
  insurance?: boolean;
  organimi?: boolean;
  comments?: string;
  status?: string;
};

const BlockHeading = styled(Text)`
  grid-column: span 2;
  margin-bottom: -8px;
`;

interface Props {
  onClose: () => void;
  department?: FreelancerDepartmentDetailsResource;
}

const FreelancerDepartmentModal: FC<Props> = ({ onClose, department }) => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const { data: users } = useQuery<FreelancerUserResource[]>(
    ['users_for_freelancers'],
    () => api.get<never, FreelancerUserResource[]>('/v1/users-for-freelancers'),
    {
      staleTime: 180 * 1000,
    },
  );

  const { data: accountNumberOptions = [] } = useAccountNumbersOptions();
  const { data: departmentOptions = [] } = useDepartmentsOptions();
  const { data: companyOptions = [] } = useCompaniesOptions();
  const { data: subunitOptions = [] } = useFreelancerSubunitsOptions();
  const { data: roleOptions = [] } = useFreelancerRolesOptions();

  const filteredDepartmentOptions = departmentOptions.filter(
    (dept) => !dept.hide_from_dropdowns,
  );

  // Keep companies already on the department even if hidden, so their chips
  // still have a label.
  const filteredCompanyOptions = companyOptions.filter(
    (p) =>
      !p.hide_from_dropdowns || !!department?.company_ids.includes(p.value),
  );

  const filteredSubunitOptions = subunitOptions.filter(
    (s) => !s.hide_from_dropdowns,
  );

  const filteredRoleOptions = roleOptions.filter((r) => !r.hide_from_dropdowns);

  const filteredTeamLeads = users
    ?.filter((user) => user.role?.name !== 'Team Member')
    .map((lead) => {
      return { label: lead.name, value: lead.id };
    });
  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      company_ids: department?.company_ids ?? [],
      department_id: department?.department_id ?? null,
      status: department?.status ?? '',
      subunit_id: department?.subunit_id ?? null,
      finance_account_number_id: department?.finance_account_number_id ?? null,
      role_id: department?.role_id ?? null,
      team_lead_id: department?.team_lead_id,
      employment_type: department?.employmentType ?? '',
      started_at: department?.startedAt ?? '',
      ended_at: department?.endedAt ?? '',
      contract_type: department?.contractType ?? '',
      trial_ended_at: department?.trialEndedAt ?? '',
      work_space: department?.workSpace ?? '',
      work_format: department?.workFormat ?? '',
      bonuses: department?.bonuses ? true : false,
      benefits: department?.benefits ? true : false,
      insurance: department?.insurance ? true : false,
      organimi: department?.organimi ? true : false,
      comments: department?.comments ?? '',
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      if (!data.company_ids.length) {
        setError('company_ids', { message: 'Select at least one company' });
        return;
      }
      try {
        if (department) {
          await api.put<FormDataT, FreelancerDepartmentDetailsResource>(
            `/v1/payments/freelancers/departments/${department.id}`,
            data,
          );
        } else {
          await api.post<FormDataT, FreelancerDepartmentDetailsResource>(
            `/v1/payments/freelancers/departments`,
            {
              freelancers_details_id: id,
              ...data,
            },
          );
        }

        await queryClient.invalidateQueries(['freelancers_details'], {
          exact: false,
        });

        await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
          exact: false,
        });

        alerts.success(
          `Freelancer's department details has been ${
            department ? 'updated' : 'created'
          } succesfully`,
        );

        onClose();
      } catch (err) {
        // Split keys so `company_ids.N` (duplicate or unknown id) lands on the
        // `company_ids` field; every other field here is flat.
        handleFormErrors<FormDataT>(err, setError, alerts, false, false);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel
        heading={`${department ? 'Edit' : 'Add'} department details`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <BlockHeading $type="caption">Department details</BlockHeading>
              <FormField<FormDataT>
                name="status"
                control={control}
                type="select"
                label="Status"
                options={statuses}
              />
              <FormField<FormDataT, number>
                name="department_id"
                control={control}
                type="select"
                label="Department"
                options={filteredDepartmentOptions}
              />
              <FormField<FormDataT, number>
                name="company_ids"
                control={control}
                type="multiselect"
                label="Companies"
                options={filteredCompanyOptions}
              />
              <FormField<FormDataT, number>
                name="subunit_id"
                control={control}
                type="select"
                label="Subunit"
                options={filteredSubunitOptions}
              />
              <FormField<FormDataT, number>
                name="finance_account_number_id"
                control={control}
                type="select"
                label="Finance Account Number"
                options={accountNumberOptions}
              />
              <FormField<FormDataT, number>
                control={control}
                name="role_id"
                label="Role"
                type="select"
                options={filteredRoleOptions}
              />
              <Larger>
                <FormField<FormDataT, number>
                  control={control}
                  name="team_lead_id"
                  label="Team Lead"
                  type="select"
                  options={filteredTeamLeads ?? []}
                />
              </Larger>
              <FormField<FormDataT>
                name="employment_type"
                control={control}
                type="select"
                label="Employment Type"
                options={employmentTypes}
              />
              <FormField<FormDataT>
                control={control}
                name="started_at"
                label="Start Date"
                type="datepicker"
                datepickerType="date"
              />
              <FormField<FormDataT>
                control={control}
                name="ended_at"
                label="End Date"
                type="datepicker"
                datepickerType="date"
              />
              <FormField<FormDataT>
                name="contract_type"
                control={control}
                type="select"
                label="Contract Type"
                options={contractTypes}
              />
              <FormField<FormDataT>
                control={control}
                name="trial_ended_at"
                label="Trial End Date"
                type="datepicker"
                datepickerType="date"
              />
              <FormField<FormDataT>
                name="work_space"
                control={control}
                type="select"
                label="Office / Work Space"
                options={workSpaces}
              />
              <FormField<FormDataT>
                name="work_format"
                control={control}
                type="select"
                label="Work Format"
                options={workFormats}
              />
              <FormField<FormDataT, boolean>
                name="bonuses"
                control={control}
                type="select"
                label="Bonuses"
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
              />
              <FormField<FormDataT, boolean>
                name="benefits"
                control={control}
                type="select"
                label="Benefits"
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
              />
              <FormField<FormDataT, boolean>
                name="insurance"
                control={control}
                type="select"
                label="Accident Insurance"
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
              />
              <FormField<FormDataT, boolean>
                name="organimi"
                control={control}
                type="select"
                label="ORGANIMI"
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
              />
              <FormField<FormDataT>
                control={control}
                type="textarea"
                name="comments"
                label="Comments"
                placeholder="Add comments"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {department ? 'Update' : 'Save'}
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerDepartmentModal;
